/**
 * Hook personalizado para gestionar lógica de cuentas contables
 * Maneja: cargar plan de cuentas, obtener configuración
 */

import { useState, useCallback } from 'react';
import axios from 'axios';

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3002') + '/api';

const getAuthHeaders = (empresaId) => {
  const token = localStorage.getItem('token');
  return {
    'empresa-id': empresaId,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export const useCuentas = (empresaId) => {
  const [cuentas, setCuentas] = useState([]);
  const [configuracion, setConfiguracion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cargar plan de cuentas
  const cargarPlanCuentas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/contabilidad/plan-cuentas`,
        { headers: getAuthHeaders(empresaId) }
      );

      const cuentasData = response.data.data || response.data || [];
      setCuentas(cuentasData);
      return cuentasData;
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      setCuentas([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  // Cargar configuración contable
  const cargarConfiguracion = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/configuracion/contable`,
        { headers: getAuthHeaders(empresaId) }
      );

      const configData = response.data.data || response.data;
      setConfiguracion(configData);
      return configData;
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      setConfiguracion(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  // Obtener cuentas por tipo
  const obtenerCuentasPorTipo = useCallback(
    (tipo) => {
      if (Array.isArray(cuentas)) {
        return cuentas.filter((c) => c.tipo === tipo);
      }
      if (cuentas.todas_las_cuentas && cuentas.todas_las_cuentas[tipo]) {
        return cuentas.todas_las_cuentas[tipo];
      }
      return [];
    },
    [cuentas]
  );

  // Obtener cuenta por ID
  const obtenerCuenta = useCallback(
    (cuentaId) => {
      if (Array.isArray(cuentas)) {
        return cuentas.find((c) => c.id === cuentaId);
      }
      // Buscar en todas las categorías
      for (const categoria of Object.values(cuentas.todas_las_cuentas || {})) {
        const cuenta = categoria.find((c) => c.id === cuentaId);
        if (cuenta) return cuenta;
      }
      return null;
    },
    [cuentas]
  );

  // Limpiar error
  const limpiarError = useCallback(() => {
    setError(null);
  }, []);

  return {
    cuentas,
    configuracion,
    loading,
    error,
    cargarPlanCuentas,
    cargarConfiguracion,
    obtenerCuentasPorTipo,
    obtenerCuenta,
    limpiarError,
  };
};
